import React from "react";
import { useIntl } from "react-intl";
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";

import Button from "../../components/button/Button";
import Input from "../../components/input/Input";
import Popup, { PopupContext } from "../../components/popup/Popup";
import { useDeviceGroupStore } from "../../store/device-group/DeviceGroupStore";

import { ReactComponent as DeleteSVG } from "../../assets/close-thin.svg";
import { ReactComponent as EfficiencySVG } from "../../assets/efficiency.svg";

import { useDialog } from "../../components/dialog/useDialog";
import useErrorAlert from "../../hooks/useErrorAlert";
import useFormatter from "../../hooks/useFormatter";
import useValidate from "../../hooks/useValidate";
import { useLoading } from "../../store/loading/LoadingContextStore";
import DeviceAutoMoveController from "./DeviceAutoMoveController";

import GroupInterface from "../../interfaces/GroupInterface";

import CustomError from "../../service/errors/custom.error";
import { MainPageOutletContext } from "../main/Main";
import "./group.scss";

function EditGroupForm({
  name,
  changeName,
  limit,
  changeLimit,
  save,
  remove,
  isNewRecord,
  groupNames = [],
}: {
  name: string;
  changeName: Function;
  limit: number | undefined;
  changeLimit: Function;
  isNewRecord: boolean;
  save: Function;
  remove: Function | undefined;
  groupNames: string[];
}) {
  const { close } = React.useContext(PopupContext);
  const { formatMessage } = useIntl();
  const { electricity_unit } = useFormatter();

  const {
    errors,
    validate,
    clear: clearErrors,
  } = useValidate(() => {
    let errors = {} as any;

    if (name.trim().length === 0) {
      errors["name"] = formatMessage({ id: "common.required" });
    } else if (
      groupNames
        .map((g) => g.trim().toLowerCase())
        .indexOf(name.trim().toLowerCase()) >= 0
    ) {
      errors["name"] = formatMessage({ id: "group.form.name.unique" });
    }

    if (limit == null || limit === 0) {
      errors["limit"] = formatMessage({ id: "common.required" });
    }

    return errors;
  });

  return (
    <form
      className="group-create-form"
      onSubmit={async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (validate() && (await save(name, limit))) {
          close();
        }
      }}
    >
      <Input
        type="text"
        label={formatMessage({ id: "group.form.name" })}
        value={name}
        onChange={(v: string) => {
          changeName(v);
          clearErrors();
        }}
        errorText={errors["name"]}
      />

      <Input
        type="integer"
        label={
          <span className="text-icon ">
            <EfficiencySVG />
            <span>{formatMessage({ id: "group.form.limit" })}</span>
          </span>
        }
        unit={electricity_unit}
        value={limit}
        onChange={(v: number | undefined) => {
          changeLimit(v);
          clearErrors();
        }}
        errorText={errors["limit"]}
      />

      <div style={{ flex: "auto" }} />

      <div className="form-controls">
        <Button
          title={
            isNewRecord
              ? formatMessage({ id: "group.form.create" })
              : formatMessage({ id: "group.form.save" })
          }
          type="submit"
          buttonType="success"
        />

        {!isNewRecord && typeof remove === "function" && (
          <Button
            Icon={DeleteSVG}
            title={formatMessage({ id: "group.form.delete" })}
            buttonType="delete"
            type="button"
            onClick={async () => {
              if (await remove()) {
                close();
              }
            }}
          />
        )}
      </div>
    </form>
  );
}

interface EditGroupPopupProps {
  isNewRecord: boolean;
  defaultName: GroupInterface["name"] | undefined;
  defaultLimit: GroupInterface["limit"] | undefined;
  groupNames: string[];
  save: Function;
  remove: Function;
}

function EditGroupPopup({
  isNewRecord,
  defaultName = "",
  defaultLimit = 16,
  groupNames,
  save,
  remove,
}: EditGroupPopupProps) {
  const navigate = useNavigate();
  const outlet = useOutletContext<MainPageOutletContext>();

  const { formatMessage } = useIntl();
  const showErrorAlert = useErrorAlert();

  const [name, changeName] = React.useState<string>(defaultName);
  const [limit, changelimit] = React.useState<number | undefined>(defaultLimit);

  return (
    <Popup
      title={
        isNewRecord
          ? formatMessage({ id: "group.form.create_title" })
          : formatMessage({ id: "group.form.edit_title" })
      }
      className="group-create-popup"
      close={() => navigate(outlet.baseUrl)}
    >
      <EditGroupForm
        isNewRecord={isNewRecord}
        name={name}
        changeName={changeName}
        limit={limit}
        changeLimit={changelimit}
        groupNames={groupNames}
        save={async (name: string, limit: number) => {
          try {
            return await save(name, limit);
          } catch (e) {
            console.error("Error when saving group", e);
            await showErrorAlert(
              e instanceof CustomError ? e.getStatusText() : undefined
            );
          }
        }}
        remove={
          !isNewRecord
            ? async () => {
                try {
                  return await remove();
                } catch (e) {
                  console.error("Error when removing group", e);
                  await showErrorAlert(
                    e instanceof CustomError ? e.getStatusText() : undefined
                  );
                }
              }
            : undefined
        }
      />
    </Popup>
  );
}

function EditGroupPopupWithState() {
  const { showConfirm } = useDialog();
  const { formatMessage } = useIntl();
  const processingAsyncFn = useLoading();

  const {
    saveDeviceGroup,
    saveGroup,
    removeGroup,
    findGroupById,
    findDeviceById,
    groupStore,
  } = useDeviceGroupStore();

  const location = useLocation();
  const { groupId } = useParams();

  const { deviceId, deviceType } = location.state ?? {};

  const group = React.useMemo(
    () => (groupId != null ? findGroupById(groupId) : undefined),
    [findGroupById, groupId]
  );

  const device = React.useMemo(() => {
    return deviceId ? findDeviceById(deviceId, deviceType) : undefined;
  }, [deviceId, deviceType, findDeviceById]);

  const groupNames = React.useMemo(
    () => groupStore.filter((g) => g.id !== group?.id).map((g) => g.name),
    [group?.id, groupStore]
  );

  const remove = React.useCallback(async () => {
    if (group?.devices?.length > 0) {
      throw new CustomError(
        406,
        formatMessage({ id: "group.remove.not_allowed" })
      );
    }

    if (
      await showConfirm({
        title: formatMessage({ id: "group.remove.confirm_title" }),
        text: formatMessage({ id: "group.remove.confirm_text" }),
        confirmLabel: formatMessage({ id: "group.remove.confirm_yes" }),
      })
    ) {
      await processingAsyncFn(() => removeGroup(group?.id));
      return true;
    }

    return false;
  }, [
    formatMessage,
    group?.devices?.length,
    group?.id,
    processingAsyncFn,
    removeGroup,
    showConfirm,
  ]);

  const save = React.useCallback(
    async (name: string, limit: number) => {
      await processingAsyncFn(async () => {
        const newGroupId = await saveGroup(group?.id, name, limit);
        if (device) {
          await saveDeviceGroup(device.id, device.type, newGroupId);
        }
      });
      return true;
    },
    [device, group?.id, processingAsyncFn, saveDeviceGroup, saveGroup]
  );

  const isNewRecord = React.useMemo(() => group?.id == null, [group?.id]);

  if (groupId != null && group == null) return null;

  if (device != null && device.groupId == null && group?.id != null) {
    return (
      <DeviceAutoMoveController
        device={device}
        group={group}
        close={() => {}}
      />
    );
  }

  return (
    <EditGroupPopup
      key={group?.id}
      isNewRecord={isNewRecord}
      defaultName={group?.name}
      defaultLimit={group?.limit}
      groupNames={groupNames}
      save={save}
      remove={remove}
    />
  );
}

export default EditGroupPopupWithState;
